/** @jsx jsx */
import ProductPage from '@bottlebooks/gatsby-theme-event/src/components/EventProductPage/EventProductPage';
import { graphql } from 'gatsby';
import deepMerge from 'lodash/merge';
import { jsx } from 'theme-ui';

export default function ProductTemplate({
  data,
  pageContext,
  location,
  navigate,
}) {
  const {
    event,
    product,
    producer,
    exhibitor,
    otherProducts,
    next,
    previous,
    siteTitle,
  } = afterQuery(data);
  return (
    <ProductPage
      {...pageContext}
      event={event}
      product={product}
      exhibitor={exhibitor}
      otherProducts={otherProducts}
      otherProductsName={producer?.name}
      previous={previous}
      next={next}
      siteTitle={siteTitle}
      location={location}
      navigate={navigate}
    />
  );
}

export const pageQuery = graphql`
  fragment predhomme_ProductTemplate_Bottlebooks_Event on Bottlebooks_Event {
    collectionId: eventId
    registeredProducts(
      filter: { productId: { eq: $productId }, partnerId: { eq: $exhibitorId } }
    ) {
      nodes {
        ...EventProductPage_RegisteredProduct
        producer {
          # TODO Add filter to exclude the current product.
          products {
            nodes {
              productId
              product {
                shortName
                bottleImage {
                  publicId
                }
                bottleVariants {
                  bottleNetWeight
                }
              }
            }
          }
        }
      }
    }
    registrations(filter: { companyId: { eq: $exhibitorId } }) {
      nodes {
        # This is a quick fix to get the data onto the page
        ...bb_Registration
      }
    }
    product(
      productId: $productId
      exhibitorId: $exhibitorId
      returnNullWhenNotFound: true
    ) {
      ...bb_EventProductPage
      ... on Bottlebooks_Wine {
        wineType(format: LOCALIZED)
      }
    }
  }

  query predhomme_ProductTemplate(
    $id: String!
    $productId: ID!
    $exhibitorId: ID!
    $locale: Bottlebooks_ContentLocale
    $previousId: String
    $nextId: String
  ) {
    bottlebooks {
      oregon: event(eventId: "62ed7ab9f28b1e10cc33ec09", locale: $locale) {
        ...predhomme_ProductTemplate_Bottlebooks_Event
      }
      washington: event(eventId: "6352fa04ba3a6c3ae627015d", locale: $locale) {
        ...predhomme_ProductTemplate_Bottlebooks_Event
      }
    }
    product(id: { eq: $id }) {
      id
      productId
      ...EventProductPage
      producer {
        name
        products {
          ...EventProductPage_Related
        }
      }
    }
    previous: product(id: { eq: $previousId }) {
      ...ChangeProductLink
    }
    next: product(id: { eq: $nextId }) {
      ...ChangeProductLink
    }
    site {
      siteMetadata {
        title
      }
    }
  }
`;

function afterQuery({
  bottlebooks,
  product,
  previous,
  next,
  site: { siteMetadata },
}) {
  const events = Object.values(bottlebooks);
  const registeredProduct = mergeBbProduct(events);
  // TODO: apply same merging logic to registrations and registered brands
  const event = events[0];
  // Pick out the registeredProduct data, so we can mix it into the base product.
  // const registeredProduct = bottlebooks?.event?.registeredProducts?.nodes?.[0];
  const exhibitor = bottlebooks?.event?.registrations?.nodes?.[0];
  const extendedProduct = deepMerge(
    {},
    { ...product, exhibitor: { ...product?.exhibitor, ...exhibitor } },
    bottlebooks?.event?.product,
    registeredProduct?.product,
    registeredProduct
  );
  const producerProducts = registeredProduct.producer?.products?.nodes || [];
  const otherProducts = producerProducts
    .filter((other) => other.productId !== product.productId)
    .slice(0, 6);

  return {
    product: extendedProduct,
    producer: extendedProduct?.producer,
    exhibitor,
    event,
    siteTitle: siteMetadata.title,
    next,
    previous,
    otherProducts,
  };
}

function mergeBbProduct(events) {
  const product = events.reduce((acc, event) => {
    const { registeredProducts, product } = event;
    if (!registeredProducts) return acc;

    const registeredProduct = registeredProducts.nodes?.[0];
    if (!registeredProduct) return acc;

    const { productId } = registeredProduct;

    acc[productId] = {
      ...registeredProduct,
      product: {
        ...registeredProduct.product,
        ...product,
      },
      collectionIds: [event.collectionId],
      standNames: [
        ...(acc[productId]?.standNames || []),
        {
          collectionId: event.collectionId,
        },
      ],
    };
    return acc;
  }, {});

  return Object.values(product)[0];
}
